import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import concesionariaVentaProduccionService from '../../../services/concesionariaVentaProduccionService';
import concesionariaAreaDespachoService from '../../../services/concesionariaAreaDespachoService';
import ConfirmModal from '../../../components/Common/ConfirmModal';
import Toast from '../../../components/Common/Toast';
import MutateGate from '../../../components/Common/MutateGate';
import CuadrosRegistradosMonthTable from './CuadrosRegistradosMonthTable';

const HUB_PATH = '/produccion-concesionaria/operaciones/produccion-dia';

const SALE_ROUTE_LABELS = {
  none: 'Sin venta',
  service_order: 'OSV',
  cash_sale: 'Venta efectivo',
};

function saleRouteLabel(route) {
  return SALE_ROUTE_LABELS[route] || route || '—';
}

function columnTaxHint(column) {
  if (!column || column.sale_route === 'none') return null;
  return column.has_igv !== false ? 'Con impuestos' : 'Sin impuestos';
}

function formatPos(pos) {
  if (!pos) return '—';
  return `${pos.code ? `${pos.code} — ` : ''}${pos.name}`;
}

function formatDateDisplay(iso) {
  if (!iso) return '—';
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split('-');
  if (!d) return s;
  return `${d}/${m}/${y}`;
}

const LEGACY_COMIDA_META_KEY = '__comida_nombre';

const emptyRow = (date, areas, implicitServiceId) => ({
  row_date: date,
  concesionaria_area_despacho_id: areas[0]?.id || '',
  comida: '',
  service_id: implicitServiceId || '',
  requerimiento: '',
  column_values: {},
});

function rowFromApi(r, implicitServiceId) {
  const cv = { ...(r.column_values || {}) };
  let comida = r.comida != null && String(r.comida).trim() !== '' ? String(r.comida) : '';
  if (!comida && cv[LEGACY_COMIDA_META_KEY] != null) {
    comida = String(cv[LEGACY_COMIDA_META_KEY]);
  }
  delete cv[LEGACY_COMIDA_META_KEY];
  return {
    row_date: r.row_date?.slice?.(0, 10) || r.row_date,
    concesionaria_area_despacho_id: r.concesionaria_area_despacho_id,
    comida,
    service_id: implicitServiceId || r.service_id || '',
    requerimiento: r.requerimiento ?? '',
    column_values: cv,
  };
}

function rowToApi(r, productionDate, implicitServiceId) {
  return {
    row_date: r.row_date || productionDate,
    concesionaria_area_despacho_id: Number(r.concesionaria_area_despacho_id),
    comida: (r.comida ?? '').trim() || null,
    service_id: Number(implicitServiceId),
    requerimiento: r.requerimiento === '' ? null : Number(r.requerimiento),
    column_values: r.column_values || {},
  };
}

function parseCuadroQty(value) {
  if (value === '' || value == null) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Diferencia = requerimiento − suma de columnas con ruta OSV; merma % sobre requerimiento. */
function computeVentaProduccionRowMetrics(row, columns) {
  const requerimiento = parseCuadroQty(row.requerimiento);
  const sumaColumnas = columns.reduce((sum, c) => {
    if (c.sale_route !== 'service_order') return sum;
    return sum + parseCuadroQty(row.column_values?.[c.column_key]);
  }, 0);
  const diferencia = requerimiento - sumaColumnas;
  const mermaPct = requerimiento > 0 ? (diferencia / requerimiento) * 100 : null;

  return { diferencia, mermaPct };
}

function formatDiferencia(value) {
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value);
  if (Math.abs(value - rounded) < 0.0005) return String(rounded);
  return value.toFixed(3).replace(/\.?0+$/, '');
}

function formatMermaPct(pct) {
  if (pct == null || !Number.isFinite(pct)) return '—';
  return `${pct.toFixed(1)}%`;
}

const ProduccionDiaHub = () => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    concesionariaVentaProduccionService.getPlantillasCatalog().then((res) => {
      if (res.success) setCatalog(res.data || []);
      setLoading(false);
    });
  }, []);

  const posGroups = useMemo(() => {
    const map = new Map();
    catalog.forEach((pl) => {
      const posId = pl.point_of_sale_id;
      if (!map.has(posId)) {
        map.set(posId, { pos: pl.point_of_sale, plantillas: [] });
      }
      map.get(posId).plantillas.push(pl);
    });
    return [...map.values()].sort((a, b) =>
      formatPos(a.pos).localeCompare(formatPos(b.pos), 'es')
    );
  }, [catalog]);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to="/produccion-concesionaria" className="hover:text-teal-600">Producción concesionaria</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Venta producción</span>
      </nav>

      <h1 className="text-2xl font-semibold text-gray-800">Venta producción</h1>
      <p className="mt-1 text-sm text-gray-500">
        Elija el punto de venta y la plantilla para ver los cuadros por día y tiempo de comida.
      </p>

      {loading ? (
        <p className="mt-6 text-gray-500">Cargando…</p>
      ) : posGroups.length === 0 ? (
        <p className="mt-6 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          No hay plantillas configuradas. Cree una en{' '}
          <Link to="/produccion-concesionaria/configuraciones/cuadro-venta" className="text-teal-700 font-medium">
            Cuadro venta (plantillas)
          </Link>
          .
        </p>
      ) : (
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          {posGroups.map(({ pos, plantillas }) => (
            <section
              key={pos?.id || plantillas[0]?.point_of_sale_id}
              className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
            >
              <div className="px-5 py-4 bg-gray-50 border-b border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900">{formatPos(pos)}</h2>
                <p className="text-sm text-gray-500">{plantillas.length} plantilla(s)</p>
              </div>
              <ul className="divide-y divide-gray-100">
                {plantillas.map((pl) => (
                  <li key={pl.id} className="px-5 py-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900">{pl.name}</p>
                      <p className="text-xs text-gray-500">{pl.columns_count ?? 0} columnas</p>
                    </div>
                    <Link
                      to={`${HUB_PATH}/plantilla/${pl.id}`}
                      className="shrink-0 px-3 py-1.5 text-sm font-medium text-teal-700 border border-teal-600 rounded-md hover:bg-teal-50"
                    >
                      Ver despachos
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const ProduccionDiaPlantillaList = ({ plantillaId }) => {
  const navigate = useNavigate();
  const [plantilla, setPlantilla] = useState(null);
  const [history, setHistory] = useState([]);
  const [pickDate, setPickDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const catRes = await concesionariaVentaProduccionService.getPlantillasCatalog();
        if (cancelled) return;
        const row = (catRes.data || []).find((p) => String(p.id) === String(plantillaId));
        setPlantilla(row || null);
        if (row) {
          const histRes = await concesionariaVentaProduccionService.getSheetsHistory({
            point_of_sale_id: row.point_of_sale_id,
            plantilla_id: row.id,
          });
          if (!cancelled && histRes.success) setHistory(histRes.data || []);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [plantillaId]);

  const openCuadro = (fecha) => {
    const q = new URLSearchParams({ fecha });
    navigate(`${HUB_PATH}/plantilla/${plantillaId}/cuadro?${q.toString()}`);
  };

  const submitCrearDespacho = (e) => {
    e?.preventDefault?.();
    if (!pickDate) {
      setToast({ isOpen: true, message: 'Indique la fecha del despacho', type: 'error' });
      return;
    }
    setCreateOpen(false);
    openCuadro(pickDate);
  };

  const createModal = createOpen
    ? createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
            aria-label="Cerrar"
            onClick={() => setCreateOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-crear-despacho-title"
            className="relative w-full max-w-md rounded-xl bg-white shadow-xl border border-gray-200"
          >
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 id="modal-crear-despacho-title" className="text-lg font-semibold text-gray-900">
                Crear despacho
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Nuevo cuadro para {plantilla?.name} · {formatPos(plantilla?.point_of_sale)}
              </p>
            </div>
            <form onSubmit={submitCrearDespacho} className="px-5 py-4 space-y-4">
              <div>
                <label htmlFor="despacho-fecha" className="block text-sm font-medium text-gray-700 mb-1">
                  Fecha del despacho
                </label>
                <input
                  id="despacho-fecha"
                  type="date"
                  value={pickDate}
                  onChange={(e) => setPickDate(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <p className="text-sm text-gray-500">
                En el cuadro registrará destino, comida y cantidades por fila; no hace falta elegir desayuno o almuerzo.
              </p>
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={!pickDate}
                  className="px-4 py-2 rounded-md bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 text-white text-sm font-medium"
                >
                  Crear despacho
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;

  if (loading) {
    return <div className="p-6 max-w-5xl mx-auto text-gray-500">Cargando…</div>;
  }

  if (!plantilla) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <p className="text-gray-700">Plantilla no encontrada.</p>
        <Link to={HUB_PATH} className="text-teal-700 font-medium mt-2 inline-block">← Volver</Link>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {createModal}
      <nav className="text-sm text-gray-600 mb-4">
        <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
        <span className="mx-2">/</span>
        <Link to={HUB_PATH} className="hover:text-teal-600">Venta producción</Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">{plantilla.name}</span>
      </nav>

      <Link to={HUB_PATH} className="text-sm text-teal-700 hover:text-teal-900 mb-2 inline-block">
        ← Puntos de venta
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">{plantilla.name}</h1>
          <p className="text-sm text-gray-500 mt-1">POS: {formatPos(plantilla.point_of_sale)}</p>
          {(plantilla.osv_client || plantilla.catalog_project) && (
            <p className="text-xs text-gray-500 mt-1">
              {plantilla.osv_client && (
                <span>
                  Cliente OSV:{' '}
                  <span className="text-gray-700">
                    {plantilla.osv_client.code ? `${plantilla.osv_client.code} — ` : ''}
                    {plantilla.osv_client.name}
                  </span>
                </span>
              )}
              {plantilla.osv_client && plantilla.catalog_project && ' · '}
              {plantilla.catalog_project && (
                <span>
                  Proyecto:{' '}
                  <span className="text-gray-700">
                    {plantilla.catalog_project.code ? `${plantilla.catalog_project.code} — ` : ''}
                    {plantilla.catalog_project.name}
                  </span>
                </span>
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 rounded-md bg-teal-600 text-white text-sm font-medium hover:bg-teal-700"
        >
          + Crear despacho
        </button>
      </div>

      <section className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900">Cuadros registrados</h2>
        <p className="text-sm text-gray-500">
          Un cuadro por fecha; agrupados por mes (expandir o contraer cada mes).
        </p>
        <CuadrosRegistradosMonthTable
          rows={history}
          onOpenCuadro={openCuadro}
          variant="simple"
          emptyMessage="Aún no hay despachos. Pulse «+ Crear despacho» para registrar el primero."
        />
      </section>

      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

const ProduccionDiaCuadroGrid = ({ plantillaId }) => {
  const [searchParams] = useSearchParams();
  const productionDate = searchParams.get('fecha') || new Date().toISOString().slice(0, 10);

  const [plantilla, setPlantilla] = useState(null);
  const [posId, setPosId] = useState('');
  const [columns, setColumns] = useState([]);
  const [comidaServices, setComidaServices] = useState([]);
  const [areas, setAreas] = useState([]);
  const [sheet, setSheet] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  const isConfirmed = sheet?.status === 'confirmed';

  useEffect(() => {
    concesionariaVentaProduccionService.getPlantillasCatalog().then((res) => {
      const row = (res.data || []).find((p) => String(p.id) === String(plantillaId));
      setPlantilla(row || null);
      if (row) setPosId(String(row.point_of_sale_id));
    });
  }, [plantillaId]);

  const loadSheet = useCallback(async () => {
    if (!posId || !plantillaId || !productionDate) return;
    setLoading(true);
    try {
      const [colRes, comRes, areaRes, sheetRes] = await Promise.all([
        concesionariaVentaProduccionService.getColumnas(plantillaId),
        concesionariaVentaProduccionService.getServiciosComida(posId),
        concesionariaAreaDespachoService.getAreas({ point_of_sale_id: posId, active_only: 1 }),
        concesionariaVentaProduccionService.getSheet({
          point_of_sale_id: posId,
          plantilla_id: plantillaId,
          production_date: productionDate,
        }),
      ]);
      if (colRes.success) setColumns((colRes.data || []).filter((c) => c.is_active));
      if (comRes.success) setComidaServices(comRes.data || []);
      if (areaRes.success) setAreas(areaRes.data || []);
      const implicitServiceId = comRes.success ? comRes.data?.[0]?.service_id : null;
      if (sheetRes.success) {
        const s = sheetRes.data;
        setSheet(s);
        if (s?.rows?.length) {
          setRows(s.rows.map((row) => rowFromApi(row, implicitServiceId)));
        } else {
          setRows([emptyRow(productionDate, areaRes.data || [], implicitServiceId)]);
        }
      }
    } catch {
      setToast({ isOpen: true, message: 'Error al cargar el cuadro', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [posId, plantillaId, productionDate]);

  useEffect(() => {
    loadSheet();
  }, [loadSheet]);

  const implicitServiceId = comidaServices[0]?.service_id ?? null;

  useEffect(() => {
    if (!implicitServiceId) return;
    setRows((prev) =>
      prev.map((r) =>
        String(r.service_id) === String(implicitServiceId) ? r : { ...r, service_id: implicitServiceId },
      ),
    );
  }, [implicitServiceId]);
  const implicitServiceLabel =
    comidaServices[0]?.service?.name ||
    (implicitServiceId ? `Servicio #${implicitServiceId}` : null);
  const implicitServicePosPrice = comidaServices[0]?.pos_price;
  const implicitServiceHasPosPrice =
    implicitServicePosPrice != null && Number(implicitServicePosPrice) > 0;

  const openConfirm = () => {
    if (!implicitServiceId) {
      setToast({
        isOpen: true,
        message: 'Configure el servicio de comida del punto de venta en Configuración.',
        type: 'error',
      });
      return;
    }
    if (!implicitServiceHasPosPrice) {
      setToast({
        isOpen: true,
        message: `Configure el precio de «${implicitServiceLabel || 'el servicio'}» en este punto de venta (Ventas / catálogo de servicios) antes de confirmar.`,
        type: 'error',
      });
      return;
    }
    setConfirmOpen(true);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const updateCell = (rowIdx, colKey, value) => {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? { ...r, column_values: { ...r.column_values, [colKey]: value === '' ? '' : Number(value) } }
          : r
      )
    );
  };

  const saveSheet = async () => {
    if (!posId) return;
    if (!implicitServiceId) {
      setToast({
        isOpen: true,
        message: 'Configure el servicio de comida del punto de venta en Configuración.',
        type: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      const res = await concesionariaVentaProduccionService.saveSheet({
        point_of_sale_id: Number(posId),
        plantilla_id: Number(plantillaId),
        production_date: productionDate,
        rows: rows.map((r, i) => ({ ...rowToApi(r, productionDate, implicitServiceId), sort_order: i })),
      });
      if (res.success) {
        setSheet(res.data);
        setToast({ isOpen: true, message: 'Borrador guardado', type: 'success' });
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo guardar',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const doConfirm = async () => {
    if (!implicitServiceId) {
      setToast({
        isOpen: true,
        message: 'Configure el servicio de comida del punto de venta en Configuración.',
        type: 'error',
      });
      setConfirmOpen(false);
      return;
    }
    if (!implicitServiceHasPosPrice) {
      setToast({
        isOpen: true,
        message: `Configure el precio de «${implicitServiceLabel || 'el servicio'}» en este punto de venta antes de confirmar.`,
        type: 'error',
      });
      setConfirmOpen(false);
      return;
    }
    setSaving(true);
    try {
      let sheetId = sheet?.id;
      if (!sheetId) {
        const saveRes = await concesionariaVentaProduccionService.saveSheet({
          point_of_sale_id: Number(posId),
          plantilla_id: Number(plantillaId),
          production_date: productionDate,
          rows: rows.map((r, i) => ({ ...rowToApi(r, productionDate, implicitServiceId), sort_order: i })),
        });
        if (saveRes.success) {
          setSheet(saveRes.data);
          sheetId = saveRes.data?.id;
        }
      }
      if (!sheetId) {
        setToast({ isOpen: true, message: 'No se pudo guardar el cuadro', type: 'error' });
        return;
      }
      const res = await concesionariaVentaProduccionService.confirmSheet(sheetId);
      if (res.success) {
        setSheet(res.data?.sheet || sheet);
        setToast({ isOpen: true, message: res.message || 'Confirmado', type: 'success' });
        await loadSheet();
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo confirmar',
        type: 'error',
      });
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const doRevert = async () => {
    if (!sheet?.id) return;
    setSaving(true);
    try {
      const res = await concesionariaVentaProduccionService.revertSheet(sheet.id);
      if (res.success) {
        setSheet(res.data);
        setToast({
          isOpen: true,
          message: res.message || 'Cuadro revertido a borrador',
          type: 'success',
        });
        await loadSheet();
      }
    } catch (e) {
      setToast({
        isOpen: true,
        message: e.response?.data?.message || 'No se pudo revertir el cuadro',
        type: 'error',
      });
    } finally {
      setSaving(false);
      setRevertOpen(false);
    }
  };

  return (
    <div className="p-6 max-w-[min(100%,1400px)] mx-auto">
      <nav className="text-sm text-gray-600 mb-4">
        <Link to={HUB_PATH} className="hover:text-teal-600">Venta producción</Link>
        <span className="mx-2">/</span>
        <Link to={`${HUB_PATH}/plantilla/${plantillaId}`} className="hover:text-teal-600">
          {plantilla?.name || 'Plantilla'}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800">Cuadro</span>
      </nav>

      <Link
        to={`${HUB_PATH}/plantilla/${plantillaId}`}
        className="text-sm text-teal-700 hover:text-teal-900 mb-2 inline-block"
      >
        ← Lista por día
      </Link>

      <h1 className="text-2xl font-semibold text-gray-800">Cuadro del día</h1>
      <p className="text-sm text-gray-500 mt-1">
        {formatPos(plantilla?.point_of_sale)} · {plantilla?.name} · {formatDateDisplay(productionDate)}
      </p>
      {implicitServiceLabel && (
        <p className="text-xs text-gray-500 mt-0.5">
          Servicio de registro (configuración del POS): {implicitServiceLabel}
          {implicitServiceHasPosPrice ? (
            <span className="text-gray-400">
              {' '}
              · Precio POS: {Number(implicitServicePosPrice).toFixed(2)}
            </span>
          ) : null}
        </p>
      )}
      {!loading && implicitServiceId && !implicitServiceHasPosPrice && (
        <p className="text-sm text-amber-800 mt-2 p-2 rounded border border-amber-200 bg-amber-50">
          Falta el precio del servicio «{implicitServiceLabel}» en este punto de venta. Configúrelo en Ventas (precios
          por POS del servicio) antes de confirmar y generar OSV o ventas.
        </p>
      )}
      {!loading && !implicitServiceId && (
        <p className="text-sm text-amber-800 mt-2 p-2 rounded border border-amber-200 bg-amber-50">
          Configure el servicio de comida del punto de venta en Producción concesionaria → Configuración antes de
          guardar o confirmar.
        </p>
      )}

      {isConfirmed && (
        <div className="mt-4 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              Cuadro confirmado. Los documentos están en Ventas (OSV / ventas).
              {sheet?.documents?.length > 0 && (
                <ul className="mt-2 list-disc pl-5">
                  {sheet.documents.map((d) => (
                    <li key={d.id}>
                      {d.document_type === 'service_order' && d.sales_service_order && (
                        <span>OSV {d.sales_service_order.code}</span>
                      )}
                      {d.document_type === 'sales_order' && d.sales_order && (
                        <span>Venta {d.sales_order.code}</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <MutateGate>
              <button
                type="button"
                disabled={saving}
                onClick={() => setRevertOpen(true)}
                className="px-3 py-1.5 text-sm font-medium rounded-md border border-orange-600 text-orange-700 bg-white hover:bg-orange-50 disabled:opacity-50"
              >
                Revertir venta
              </button>
            </MutateGate>
          </div>
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-gray-500">Cargando…</p>
      ) : (
        <>
          <div className="mt-6 overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left">Destino</th>
                  <th className="px-2 py-2 text-left">Comida</th>
                  <th className="px-2 py-2 text-left">Requerimiento</th>
                  {columns.map((c) => (
                    <th key={c.column_key} className="px-2 py-2 text-left whitespace-nowrap">
                      {c.label}
                      <span className="block text-[10px] text-gray-400 font-normal">{saleRouteLabel(c.sale_route)}</span>
                      {columnTaxHint(c) && (
                        <span className="block text-[10px] text-teal-700 font-normal">{columnTaxHint(c)}</span>
                      )}
                    </th>
                  ))}
                  <th className="px-2 py-2 text-right whitespace-nowrap bg-orange-100 text-gray-900">Diferencia</th>
                  <th className="px-2 py-2 text-right whitespace-nowrap bg-red-100 text-gray-900">Merma</th>
                  {!isConfirmed && <th className="w-8" />}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  const { diferencia, mermaPct } = computeVentaProduccionRowMetrics(row, columns);
                  return (
                  <tr key={idx} className="border-t border-gray-100">
                    <td className="px-2 py-1">
                      <select
                        disabled={isConfirmed}
                        value={row.concesionaria_area_despacho_id}
                        onChange={(e) => updateRow(idx, { concesionaria_area_despacho_id: e.target.value })}
                        className="min-w-[140px] border border-gray-200 rounded px-1 py-0.5 text-xs"
                      >
                        <option value="">Destino…</option>
                        {areas.map((a) => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="text"
                        disabled={isConfirmed}
                        value={row.comida ?? ''}
                        onChange={(e) => updateRow(idx, { comida: e.target.value })}
                        placeholder="Ej. arroz, pollo…"
                        className="min-w-[140px] border border-gray-200 rounded px-1 py-0.5 text-xs"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        disabled={isConfirmed}
                        value={row.requerimiento}
                        onChange={(e) => updateRow(idx, { requerimiento: e.target.value })}
                        className="w-20 border border-gray-200 rounded px-1 py-0.5 text-xs"
                      />
                    </td>
                    {columns.map((c) => (
                      <td key={c.column_key} className="px-2 py-1">
                        <input
                          type="number"
                          min="0"
                          step="0.001"
                          disabled={isConfirmed}
                          value={row.column_values?.[c.column_key] ?? ''}
                          onChange={(e) => updateCell(idx, c.column_key, e.target.value)}
                          className="w-20 border border-gray-200 rounded px-1 py-0.5 text-xs"
                        />
                      </td>
                    ))}
                    <td
                      className={`px-2 py-1 text-right tabular-nums text-xs font-medium ${
                        diferencia < 0 ? 'bg-red-50 text-red-700' : ''
                      }`}
                    >
                      {formatDiferencia(diferencia)}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums text-xs">
                      {formatMermaPct(mermaPct)}
                    </td>
                    {!isConfirmed && (
                      <td className="px-2 py-1">
                        <button
                          type="button"
                          className="text-red-600 text-xs"
                          onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {!isConfirmed && (
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  setRows((prev) => [...prev, emptyRow(productionDate, areas, implicitServiceId)])
                }
                className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
              >
                + Fila
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={saveSheet}
                className="px-4 py-2 text-sm bg-white border border-teal-600 text-teal-700 rounded-md hover:bg-teal-50"
              >
                Guardar borrador
              </button>
              <button
                type="button"
                disabled={saving || !implicitServiceId || !implicitServiceHasPosPrice}
                title={
                  !implicitServiceHasPosPrice && implicitServiceId
                    ? 'Configure el precio del servicio en el POS'
                    : undefined
                }
                onClick={openConfirm}
                className="px-4 py-2 text-sm bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirmar y generar ventas
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={confirmOpen}
        title="Confirmar cuadro"
        message="Se generará OSV por requerimiento (por destino). Venta en efectivo con impuestos → boleta; sin impuestos → otro documento. ¿Continuar?"
        confirmText="Confirmar"
        onConfirm={doConfirm}
        onClose={() => setConfirmOpen(false)}
        isLoading={saving}
      />
      <ConfirmModal
        isOpen={revertOpen}
        title="Revertir venta del cuadro"
        message="Se anularán la OSV y las ventas generadas desde este cuadro y volverá a modo borrador para editar. ¿Continuar?"
        confirmText="Revertir"
        onConfirm={doRevert}
        onClose={() => !saving && setRevertOpen(false)}
        isLoading={saving}
      />
      <Toast {...toast} onClose={() => setToast((t) => ({ ...t, isOpen: false }))} />
    </div>
  );
};

const ProduccionDia = () => {
  const { plantillaId } = useParams();
  const location = useLocation();
  const isCuadro = location.pathname.includes('/cuadro');

  if (!plantillaId) {
    return <ProduccionDiaHub />;
  }
  if (isCuadro) {
    return <ProduccionDiaCuadroGrid plantillaId={plantillaId} />;
  }
  return <ProduccionDiaPlantillaList plantillaId={plantillaId} />;
};

export default ProduccionDia;
